"use client";

import type { SupTransactionType, Networkish, SupWeb3TxSingleVault, SupWeb3Balance, SupWithdrawableBalance, SupWithdrawableToken } from "@/types";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { trackTxFinished } from "@/utils/analytics";
import { useRouter } from "next/navigation";
import { useDynamicContext } from "@dynamic-labs/sdk-react-core";
import { useStoredData, useStoredDataDispatch } from "@/store/Context";
import { mapAddressWithSuperScan } from "@/utils";
import useWeb3UserAddress from "@/hooks/web3/useWeb3UserAddress";
import useMutateSuperpositionBalances from "@/hooks/mutators/useMutateTokenSuperpositionBalances";
import useMutateTransactions from "@/hooks/mutators/useMutateTransactions";

import { SupLinkExternal, SupText } from "@repo/core-ui";
import UiLabelChainScanLink from "@/components/Ui/Label/ChainScanLink";

interface Web3CommonFinishedProps {
  transferTx: string | undefined;
  chainId: Networkish;
  type: SupTransactionType;
  vaultList: SupWeb3TxSingleVault[];
  selectedBalance?: SupWeb3Balance | SupWithdrawableBalance | SupWithdrawableToken;
}

export default function Web3CommonFinished({ transferTx, chainId, type, vaultList, selectedBalance }: Web3CommonFinishedProps) {
  const { network } = useDynamicContext();
  const storedDispatch = useStoredDataDispatch();
  const storedData = useStoredData();
  const userAddress = useWeb3UserAddress();
  const { push } = useRouter();

  const [nonCrossChain, setNonCrossChain] = useState<boolean>(false);
  const [isRetain4626, setIsRetain4626] = useState<boolean>(false);
  const [completeWithdrawalLink, setCompleteWithdrawalLink] = useState<string | undefined>();

  const preparedChain = useMemo(() => chainId || network, [chainId, network]);

  const { mutate } = useMutateSuperpositionBalances();
  const { mutate: mutateTxs } = useMutateTransactions();

  useEffect(() => {
    const isSameChain = vaultList.filter(vault => vault.chain.id !== preparedChain).length === 0;
    setNonCrossChain(isSameChain);
    if ((vaultList.some(vault => vault.recommend_vault_share_withdrawal) || (storedData.txSettings.retain4626 ?? false)) && type === "withdraw") {
      const foundTwoStepVault = vaultList.find(vault => vault.recommend_vault_share_withdrawal && !!vault.withdrawal_link);
      if (foundTwoStepVault) {
        setCompleteWithdrawalLink(foundTwoStepVault.withdrawal_link);
      }
      setIsRetain4626(true);
    }

    if (transferTx) {
      trackTxFinished(transferTx, vaultList, type, userAddress, chainId, selectedBalance);
    }

    storedDispatch({ type: "setTxCartValue" });
    storedDispatch({ type: type === "deposit" ? "clearCart" : "clearWithdrawCart" });
    const timeout = setTimeout(() => {
      mutate();
      mutateTxs();
    }, 5000);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line
  }, []);

  const handleClose = () => {
    storedDispatch({ type: "closeOverlay" });
  };

  const handleActivityClick = useCallback(() => {
    push(`/activity/tx/${transferTx}`);
    storedDispatch({ type: "closeOverlay" });
  }, [push, storedDispatch, transferTx]);

  return (
    <>
      <div className="my-2 flex w-full flex-col items-start justify-center space-y-2">
        {!nonCrossChain && (
          <div className="bg-info-500 p-4 text-neutral-900">
            <SupText variant="subtitle2">
              Cross-chain transactions will take <strong>at least 30 minutes</strong>&nbsp;
              {type === "withdraw" ? "to complete" : "to mint your SuperPositions"}. No further actions are required.
            </SupText>
          </div>
        )}
        {isRetain4626 && (
          <div className="border-0.5 border-neutral-300 bg-neutral-50 p-4">
            <SupText variant="subtitle2" className="space-y-3 normal-case" as="div">
              <p>This is a 2-step withdrawal vault.</p>
              <p>
                After your withdrawal on Superform is complete, you will also have to withdraw from the protocol’s site. The protocol’s withdrawal link will be provided on the
                activity page and SuperScan.
              </p>
            </SupText>
          </div>
        )}
        {transferTx && preparedChain && <UiLabelChainScanLink address={`${transferTx}`} chain={Number(preparedChain)} />}
        {transferTx && mapAddressWithSuperScan(transferTx as string, handleActivityClick, true)}
        {completeWithdrawalLink && (
          <a
            href={completeWithdrawalLink}
            target="_blank"
            className="linkstyle group inline-flex items-center whitespace-nowrap font-light text-neutral-900 decoration-from-font lg:font-normal"
            rel="noopener noreferrer"
          >
            <SupText variant="subtitle2">COMPLETE WITHDRAWAL</SupText>
            <SupLinkExternal className="ml-1 h-4 stroke-0" />
          </a>
        )}
      </div>
      <div className="mt-auto flex w-full items-center justify-start space-x-2">
        <button className="btn-neutral-600" onClickCapture={handleClose}>
          OK!
        </button>
      </div>
    </>
  );
}
